import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images } from '../config/esanzaContent';
import { positioning, traceChain, ctas } from '../config/product';
import { contentText, useWebsiteContent } from '../hooks/useWebsiteContent';

const TraceabilityPage: React.FC = () => {
  const { t } = useTranslation();
  const { page } = useWebsiteContent('traceability');
  const steps = t('site.traceability.steps', { returnObjects: true }) as Array<[string, string]>;
  const outcomes = t('site.traceability.outcomes', { returnObjects: true }) as string[];
  const label = contentText(page, 'focus', t('site.traceability.label', 'Farm-to-buyer traceability'));
  const headline = page?.title || t('site.traceability.title', 'From field activity to final buyer.');
  const subtext =
    page?.summary ||
    t(
      'site.traceability.subtitle',
      'eSanza turns everyday field activity into trusted records for buyers, partners and future finance.'
    );
  const readinessText = t(
    'site.traceability.readiness_text',
    'Field teams register farms and crops, record activities, document training, prepare harvest lots, and show buyers exactly where supply comes from. This creates the evidence needed for scale, finance and stronger market partnerships.'
  );

  return (
    <>
      <SEO
        title="Traceability | eSanza"
        description="End-to-end agricultural traceability: farm, field, crop, activity, harvest, lot, warehouse, shipment and buyer — recorded once and reused everywhere."
        imagePath={images.tree}
        keywords="farm traceability software, agricultural traceability, supply chain traceability, harvest lot tracking, farm GIS"
        url="https://esanza.com/traceability"
      />
      <PageShell>
        <section className="relative overflow-hidden px-5 pb-20 pt-32 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">{label}</p>
              <h1 className="mt-5 text-5xl font-black leading-[1] tracking-tight md:text-6xl">{headline}</h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-white/68">{subtext}</p>
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-white/50">{positioning.traceability}</p>
            </div>
            <img
              src={images.traceabilityPhoto}
              alt="Traceability records moving from the field towards the buyer"
              className="rounded-lg border border-white/12 shadow-2xl shadow-black/35"
            />
          </div>
        </section>

        {/* Full chain */}
        <section className="bg-[#0a1f14] px-5 py-16 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">The chain</p>
            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              {traceChain.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm font-bold text-white/85">
                    {step}
                  </span>
                  {i < traceChain.length - 1 && (
                    <span className="text-[#d4af37]" aria-hidden="true">
                      →
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f7f9f4] px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div className="grid gap-4 sm:grid-cols-2">
              {steps.map(([title, text]) => (
                <div key={title} className="rounded-lg border border-[#dbe5d2] bg-white p-7">
                  <h2 className="text-xl font-black">{title}</h2>
                  <p className="mt-3 text-sm leading-6 text-[#596959]">{text}</p>
                </div>
              ))}
            </div>
            <div>
              <img
                src={images.tree}
                alt="Field and tree records feeding traceability"
                loading="lazy"
                className="rounded-lg border border-[#dbe5d2] shadow-xl"
              />
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {outcomes.map((outcome) => (
                  <div key={outcome} className="rounded-lg border border-[#dbe5d2] bg-white p-4 text-sm font-extrabold text-[#14532d]">
                    {outcome}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">
                {t('site.traceability.readiness_label', 'Deployment status')}
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">
                {t('site.traceability.readiness_title', 'Designed for proof, not promises.')}
              </h2>
              <p className="mt-5 text-base leading-7 text-[#596959]">{readinessText}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={ctas.primary.to}
                  className="rounded-full bg-[#14532d] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]"
                >
                  {ctas.primary.label}
                </Link>
                <Link to="/marketplace" className="rounded-full border border-[#14532d]/25 bg-[#14532d]/[0.06] px-7 py-3.5 text-sm font-bold text-[#14532d] transition hover:bg-[#14532d]/10">
                  {ctas.tertiary.label}
                </Link>
              </div>
            </div>
            <img src={images.traceability} alt="eSanza traceability workflow" loading="lazy" className="rounded-lg border border-[#dbe5d2] shadow-xl" />
          </div>
        </section>
      </PageShell>
    </>
  );
};

export default TraceabilityPage;
